/**
 * What a file upload may be, and what to tell the user when the backend's
 * presign (`POST /file_data_source_upload`) refuses one. Shared by the webapp
 * chat attachment and the extension's two upload sites so all three say the
 * same thing. No platform: the error is read structurally, never as an axios
 * type.
 */

/** Mirrors `MAX_FILE_BYTES` in dooglie-backend `doogly/files/convert.py`. */
export const MAX_UPLOAD_MB = 100;
export const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;

/** Any failure that is not one of the two refusals below. */
export const UPLOAD_FAILED_MESSAGE = 'Upload failed. Please try again.';

export const FILE_TOO_LARGE_MESSAGE = `This file is too large — the limit is ${MAX_UPLOAD_MB} MB.`;

const UNSUPPORTED_FALLBACK = "this kind of file can't be read";

/** The first file over the limit, or `undefined`: the client-side pre-check. */
export function firstOversizedFile<T extends { size: number }>(
  files: readonly T[]
): T | undefined {
  return files.find((f) => f.size > MAX_UPLOAD_BYTES);
}

function unsupportedMessage(reason: unknown): string {
  const text =
    typeof reason === 'string' ? reason.trim().replace(/[.\s]+$/, '') : '';
  return `This file type isn't supported — ${text || UNSUPPORTED_FALLBACK}.`;
}

/**
 * The message for a presign refused with `file_too_large` or
 * `unsupported_file_type` (HTTP 400, `{error, files: [...]}`; an axios error or
 * the unwrapped body), or `null` for any other error, which keeps the generic message.
 */
export function uploadRefusalMessage(error: unknown): string | null {
  const data = refusalBody(error);
  if (data?.error === 'file_too_large') return FILE_TOO_LARGE_MESSAGE;
  if (data?.error === 'unsupported_file_type') {
    const first = Array.isArray(data.files) ? data.files[0] : undefined;
    return unsupportedMessage((first as { reason?: unknown } | undefined)?.reason);
  }
  return null;
}

/**
 * The refusal body from either shape a failed call arrives in: an axios error
 * (`error.response.data`), or the body itself, which both apps' response
 * interceptors reject with once they have unwrapped the axios error.
 */
function refusalBody(error: unknown): { error?: unknown; files?: unknown } | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const response = (error as { response?: unknown }).response;
  const body =
    typeof response === 'object' && response !== null
      ? (response as { data?: unknown }).data
      : error;
  return typeof body === 'object' && body !== null
    ? (body as { error?: unknown; files?: unknown })
    : undefined;
}

/** The refusal's message when there is one, else the generic failure. */
export function uploadErrorMessage(error: unknown): string {
  return uploadRefusalMessage(error) ?? UPLOAD_FAILED_MESSAGE;
}
